/**
 * Company access store (SC-409): the last answer from `GET /api/company/me/access` for the
 * signed-in user. MainMenu derives the suspended-company gate from it.
 *
 * Kept free of imports that lead back to `authService`, which clears this store on sign-out.
 */
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import Parse from 'parse'
import {
  fetchCompanyAccess,
  registerCompanyAccessRefusalHandler,
  type CompanyAccess,
} from '../services/CompanyAccessService'

/**
 * - idle: nothing fetched yet for this user.
 * - loading: the first fetch is in flight and there is no answer to show.
 * - ready: `access` holds an answer (a later failed refresh keeps it).
 * - error: the first fetch failed, so there is no answer; the UI renders as before the gate.
 */
export type CompanyAccessStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface CompanyAccessState {
  access: CompanyAccess | null
  /** Who `access` belongs to; an answer for another user is never applied. */
  userId: string | null
  status: CompanyAccessStatus
  lastFetchedAt: number
  /**
   * Single-flight: concurrent callers share one request. `maxAgeMs` lets a caller skip the
   * request when the current answer is recent enough.
   */
  refresh: (opts?: { maxAgeMs?: number }) => Promise<void>
  clear: () => void
}

const INITIAL = {
  access: null,
  userId: null,
  status: 'idle' as CompanyAccessStatus,
  lastFetchedAt: 0,
}

let inflight: { userId: string; promise: Promise<void> } | null = null
/** Bumped by clear(); a request started before a clear must not repopulate the store. */
let generation = 0

function currentUserId(): string | null {
  try {
    return Parse.User.current()?.id ?? null
  } catch {
    return null
  }
}

export const useCompanyAccessStore = create<CompanyAccessState>()(
  subscribeWithSelector((set, get) => ({
    ...INITIAL,

    refresh: (opts = {}) => {
      const userId = currentUserId()
      if (!userId) {
        get().clear()
        return Promise.resolve()
      }

      const state = get()
      if (state.userId && state.userId !== userId) {
        // Another account signed in without a sign-out (e.g. an invitation redeemed in a
        // browser that held an admin session): the old answer is not this user's.
        set({ ...INITIAL })
      }

      const fresh = get()
      if (
        opts.maxAgeMs !== undefined &&
        fresh.status === 'ready' &&
        fresh.userId === userId &&
        Date.now() - fresh.lastFetchedAt < opts.maxAgeMs
      ) {
        return Promise.resolve()
      }

      if (inflight && inflight.userId === userId) return inflight.promise

      const startedGeneration = generation
      if (!fresh.access) set({ status: 'loading', userId })

      const promise = (async () => {
        try {
          const access = await fetchCompanyAccess()
          if (startedGeneration !== generation || currentUserId() !== userId) return
          set({ access, userId, status: 'ready', lastFetchedAt: Date.now() })
        } catch (error) {
          if (startedGeneration !== generation || currentUserId() !== userId) return
          console.warn('[companyAccessStore] Access check failed; keeping the last known state', error)
          const current = get()
          if (!(current.access && current.userId === userId)) {
            set({ status: 'error', userId })
          }
        } finally {
          if (inflight?.promise === promise) inflight = null
        }
      })()

      inflight = { userId, promise }
      return promise
    },

    clear: () => {
      generation += 1
      inflight = null
      set({ ...INITIAL })
    },
  }))
)

/** Called from `authService.signOut`, next to the other per-user caches. */
export function clearCompanyAccessState(): void {
  useCompanyAccessStore.getState().clear()
}

// A refusal from a backend (cloud 119, Colyseus join, chat token, company lobby) means the
// answer we hold is stale: re-check now rather than waiting for focus or the next poll.
registerCompanyAccessRefusalHandler(() => {
  void useCompanyAccessStore.getState().refresh()
})
